import React, { useState, useEffect } from 'react';
import { X, Gift, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface ExitIntentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExitIntentModal: React.FC<ExitIntentModalProps> = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const couponCode = "WELCOME";

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setCopied(true);
      toast.success('Gutscheincode kopiert!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Fehler beim Kopieren des Codes');
    }
  };

  const handleStay = () => {
    handleClose();
    toast.success('Schön, dass du bleibst! 🌱');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative bg-white rounded-xl shadow-lg max-w-sm w-full mx-4 p-6 transition-all duration-300 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1.5 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>

        {/* Content */}
        <div className="text-center space-y-5">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <Gift className="w-6 h-6 text-emerald-600" />
            </div>
          </div>

          {/* Headline */}
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Moment noch! 🌱
            </h2>
            <p className="text-sm text-gray-600">
              Bevor du gehst - hier ist ein kleines Dankeschön für deinen Besuch.
            </p>
          </div>

          {/* Coupon */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
            <div className="text-xs font-medium text-emerald-700">
              Willkommensbonus
            </div>
            <div className="flex items-center justify-center space-x-2 bg-white rounded-md p-2 border border-emerald-100">
              <span className="text-lg font-bold text-emerald-600 tracking-wide font-mono">
                {couponCode}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyCode}
                className="p-1.5 hover:bg-emerald-50"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-emerald-500" />
                )}
              </Button>
            </div>
          </div>

          {/* Benefits */}
          <div className="text-left space-y-2">
            <h3 className="text-sm font-medium text-gray-900 text-center">
              Was dich erwartet:
            </h3>
            <ul className="space-y-1.5 text-xs text-gray-600">
              <li className="flex items-start space-x-2">
                <div className="w-1 h-1 bg-emerald-500 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>30-Tage-Bedenkzeit für bewusste Kaufentscheidungen</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1 h-1 bg-emerald-500 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Sparziele setzen und Fortschritt verfolgen</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1 h-1 bg-emerald-500 rounded-full mt-1.5 flex-shrink-0"></div>
                <span>Pro & Contra Listen für bessere Entscheidungen</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <Button
              onClick={handleStay}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm py-2.5 rounded-lg transition-colors"
            >
              Jetzt ausprobieren
            </Button>
            <Button
              variant="ghost"
              onClick={handleClose}
              className="w-full text-gray-400 hover:text-gray-600 text-xs"
            >
              Nein danke
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}; 